<template>
  <div class="d-contents">
    <v-menu v-if="!row.row.group_level" v-model="showDropdown" offset-x>
      <template v-slot:activator="{ on, attrs }">
        <div
          :id="`${cellId}-wrapper`"
          v-bind="contentEditable ? attrs : null"
          :style="gridStyle"
          class="tender-list-cell tender-list-cell_value tender-list-cell_value-vertical"
          :class="{
            'tender-list-cell_success': isBestPriceCell,
            'cell-can-be-edited': contentEditable,
          }"
          :contenteditable="contentEditable"
          v-on="contentEditable ? on : null"
          @blur="handleBlur"
          @keydown="keydownCellValueHandler"
          @paste="keydownCellValue"
          @click="focusValue"
        >
          <v-skeleton-loader v-if="tenderListsInfoLoading" type="text" width="50" />
          <div v-else class="d-contents">
            <div ref="cellValueWrapper" class="tender-list-cell_currency spacex-4">
              <div class="table-cell-currency" contenteditable="false">
                {{ currencySymbols[currency] }}
              </div>
              <div
                :id="cellId"
                :key="value"
                ref="cellValue"
                class="tender-list-cell_value-text"
                @click.stop="valueClickHandler"
              >
                {{ addSpaces(value) }}
              </div>
            </div>
            <!--  Показывать компанию только у тех ячеек, которые можно кастомизировать (исключаем Total)  -->
            <div
              v-if="companyNameCell && contentEditable"
              class="tender-list-cell_company-name spacex-4"
              :contenteditable="false"
            >
              <div class="tender-list-cell_company-name-text">{{ companyNameCell.text }}</div>
              <div v-if="companyNameCell.version">{{ companyNameCell.version }}</div>
            </div>
          </div>
        </div>
      </template>
      <TenderOffersVersionsList
        v-if="offers.length"
        :offers="offers"
        :show-all-versions="false"
        :available-tender-offer-version-ids="row.available_tender_offer_version_ids"
        @change-general-selected-version="(info) => $emit('change-general-selected-version', info)"
      />
    </v-menu>
    <div v-else :style="gridStyle" class="tender-list-cell tender-list-cell__parent-row" />
  </div>
</template>

<script>
  import { VSkeletonLoader, VMenu } from 'vuetify/lib';
  import { mapActions, mapState } from 'vuex';
  import TenderOffersVersionsList from '@/containers/tender/tender-list/TenderOffersVersionsList';
  import contenteditableCell from '@/utils/mixins/contenteditable-cell';
  import addSpaces from '@/utils/formatting/add-spaces';
  import tableNavigation from '@/utils/mixins/table-navigation';
  import TABLES_IDS from '@/utils/constants/tables-ids';

  export default {
    name: 'TenderListGeneralEstimateCell',
    components: {
      TenderOffersVersionsList,
      VSkeletonLoader,
      VMenu,
    },
    props: {
      value: {
        type: String,
        required: true,
      },
      row: {
        type: Object,
        required: true,
      },
      currency: {
        type: String,
        required: true,
      },
      field: {
        type: String,
        required: true,
      },
      offers: {
        type: Array,
        required: true,
      },
      contentEditable: {
        type: Boolean,
        required: true,
      },
      gridStyle: {
        type: Object,
      },
      orderOfRowsIds: {
        type: Array,
        default: () => [],
      },
    },
    mixins: [contenteditableCell, tableNavigation],
    data() {
      return {
        isNumber: true,
        // используется в миксине
        MAX_NUMBER_LENGTH: 9,
        orderOfFieldsToEdit: ['labour_total', 'materials_total', 'tools_total'],
      };
    },
    computed: {
      ...mapState('global', ['currencySymbols']),
      ...mapState('tender', ['tenderListsInfoLoading']),
      ...mapState('focusedValue', ['focusedValue']),
      isBestPriceCell() {
        return !!(
          (this.field === 'labour_total' && this.row.row.is_min_labour_price) ||
          (this.field === 'materials_total' && this.row.row.is_min_materials_price) ||
          (this.field === 'tools_total' && this.row.row.is_min_tools_price) ||
          (this.field === 'total_total' && this.row.row.is_min_price)
        );
      },
      companyNameCell() {
        const localRow = this.row?.row;

        // Компоненты возвращаемого текста
        let companyName = '';
        let versionName = '';

        // В структуре строки имеются флаги принадлежности конкретного поля (или всех полей - "all")
        // к конкретной компании или указание что цена уцказана вручную
        // Исходя из этой информации мы сначала проверяем информацию для всей строки по умолчанию и потом проверяем
        // флаг каждого поля, собирая название компании и название версии в структуре строки
        const allCellsInfo = localRow?.tender_sources?.find((info) => info?.column === 'all');
        if (allCellsInfo) {
          const allOfferVersion = allCellsInfo?.tender_offer_version;
          if (allOfferVersion) {
            [companyName, versionName] = this.getNames(allOfferVersion);
          }
        }

        if (
          this.field === 'labour_total' &&
          this.companyNameCellFieldTextByRow(localRow, 'labour')
        ) {
          return this.companyNameCellFieldTextByRow(localRow, 'labour');
        }

        if (
          this.field === 'materials_total' &&
          this.companyNameCellFieldTextByRow(localRow, 'materials')
        ) {
          return this.companyNameCellFieldTextByRow(localRow, 'materials');
        }

        if (this.field === 'tools_total' && this.companyNameCellFieldTextByRow(localRow, 'tools')) {
          return this.companyNameCellFieldTextByRow(localRow, 'tools');
        }

        return this.companyNameCellFieldText([companyName, versionName]);
      },
    },
    methods: {
      ...mapActions('focusedValue', ['setFocusedValue']),
      handleBlur($event) {
        this.blurCellValue($event, { rowId: this.row.id, key: this.field });

        let newVal = this.$refs.cellValue.innerText?.trim();
        if (!newVal.length || !parseInt(newVal)) {
          this.$refs.cellValue.innerText = addSpaces(0);
        }
      },
      addSpaces,
      // Возвращает массив-пару названий компании и версии
      getNames(version) {
        const companyName = version?.tender_offer?.contractor_team?.name;
        const versionName = version?.name;
        return [companyName, versionName];
      },
      companyNameCellFieldText([companyName, versionName]) {
        return {
          text: `${companyName || ''}`,
          version: `${versionName ? this.$t('v.') : ''}${versionName || ''}`,
        };
      },
      companyNameCellFieldTextByRow(localRow, field) {
        const cellInfo = localRow?.tender_sources?.find((info) => info?.column === field);
        if (cellInfo?.is_custom_price) {
          return { text: this.$t('My price') };
        }
        const offerVersion = cellInfo?.tender_offer_version;
        if (offerVersion && this.field === `${field}_total`) {
          return this.companyNameCellFieldText(this.getNames(offerVersion));
        }
      },
      async keydownCellValueHandler(e) {
        // Возвращается или айди новой ячейки или null, если перемещение не требуется
        const newCellId = await this.keydownCellValueMixinHandler(e, {
          rowsIds: this.orderOfRowsIds,
          fields: this.orderOfFieldsToEdit,
          currentRowId: this.row.id,
          currentField: this.field,
          isBreakdown: false,
          currentCellId: this.cellId,
          value: this.value,
          tableId: TABLES_IDS.TENDER_TABLE_ID,
        });
        // При наличии нового айди (другой ячейки) меняем фокус
        if (newCellId && newCellId !== this.cellId) {
          await this.focusValue(e, newCellId);
        }
      },
    },
  };
</script>
